import React, { useState, useEffect } from 'react';
import { Calendar, Plus, Trash2, AlertCircle, CheckCircle } from 'lucide-react';
import { getAllPromos, createPromo, deletePromo } from '../api/promoConfigApi';

export default function AdminPromoConfig() {
    const [promos, setPromos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({
        startDate: '',
        endDate: '',
        discountPercentage: ''
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        loadPromos();
    }, []);

    const loadPromos = async () => {
        setLoading(true);
        const data = await getAllPromos();
        setPromos(data);
        setLoading(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        // Validation
        if (!formData.startDate || !formData.endDate || !formData.discountPercentage) {
            setError('Todos los campos son requeridos');
            return;
        }

        if (new Date(formData.endDate) <= new Date(formData.startDate)) {
            setError('La fecha de fin debe ser posterior a la fecha de inicio');
            return;
        }

        try {
            await createPromo(formData);
            setSuccess('Promoción creada exitosamente');
            setFormData({ startDate: '', endDate: '', discountPercentage: '' });
            loadPromos();
        } catch (err) {
            setError('Error al crear la promoción');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Estás seguro de eliminar esta promoción?')) return;

        try {
            await deletePromo(id);
            setSuccess('Promoción eliminada');
            loadPromos();
        } catch (err) {
            setError('Error al eliminar la promoción');
        }
    };

    const isPromoActive = (promo) => {
        const now = new Date();
        // Append time to ensure comparison is based on full days in local time
        const start = new Date(promo.startDate + 'T00:00:00');
        const end = new Date(promo.endDate + 'T23:59:59');
        return now >= start && now <= end;
    };

    const getPromoStatus = (promo) => {
        const now = new Date();
        const start = new Date(promo.startDate + 'T00:00:00');
        const end = new Date(promo.endDate + 'T23:59:59');

        if (now < start) return { label: 'Próxima', color: 'text-blue-600 bg-blue-50' };
        if (now > end) return { label: 'Finalizada', color: 'text-gray-600 bg-gray-50' };
        return { label: 'Activa', color: 'text-green-600 bg-green-50' };
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-gray-500">Cargando...</div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-800 mb-2">Activar precio preferencial</h1>
                <p className="text-gray-600">
                    Gestiona los períodos promocionales donde se activa el precio preferencial en efectivo
                </p>
            </div>

            {/* Alerts */}
            {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <span className="text-red-700">{error}</span>
                </div>
            )}

            {success && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-green-700">{success}</span>
                </div>
            )}

            {/* Create Form */}
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-purple-100">
                <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <Plus className="w-5 h-5 text-purple-600" />
                    Nuevo período promocional
                </h2>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Fecha de Inicio
                            </label>
                            <input
                                type="date"
                                value={formData.startDate}
                                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                                className="w-full px-4 py-2 border-2 border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-400"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Fecha de Fin
                            </label>
                            <input
                                type="date"
                                value={formData.endDate}
                                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                                className="w-full px-4 py-2 border-2 border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-400"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                % Descuento
                            </label>
                            <input
                                type="number"
                                placeholder="Ej: 10"
                                value={formData.discountPercentage}
                                onChange={(e) => setFormData({ ...formData, discountPercentage: e.target.value })}
                                className="w-full px-4 py-2 border-2 border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-400"
                                required
                            />
                        </div>
                    </div>

                    <button
                        type="submit"
                        className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-xl font-semibold transition-all shadow-lg"
                    >
                        Crear período promocional
                    </button>
                </form>
            </div>

            {/* Promos List */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-purple-100">
                <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-purple-600" />
                    Períodos configurados
                </h2>

                {promos.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No hay promociones configuradas</p>
                ) : (
                    <div className="space-y-3">
                        {promos.map((promo) => {
                            const status = getPromoStatus(promo);
                            return (
                                <div
                                    key={promo.id}
                                    className={`p-4 rounded-xl border-2 ${isPromoActive(promo) ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-gray-50'
                                        }`}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-2">
                                                <span className={`px-3 py-1 rounded-full text-xs font-bold ${status.color}`}>
                                                    {status.label}
                                                </span>
                                                {promo.discountPercentage && (
                                                    <span className="px-3 py-1 rounded-full text-xs font-bold bg-pink-100 text-pink-600">
                                                        {promo.discountPercentage}% OFF
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-sm text-gray-700">
                                                <span className="font-semibold">Desde:</span>{' '}
                                                {promo.startDate.split('-').reverse().join('/')}
                                                {' '}<span className="font-semibold">Hasta:</span>{' '}
                                                {promo.endDate.split('-').reverse().join('/')}
                                            </div>
                                        </div>

                                        <button
                                            onClick={() => handleDelete(promo.id)}
                                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition"
                                            title="Eliminar promoción"
                                        >
                                            <Trash2 className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
